// Import tailwind and global css
// Note that Tailwind should be imported into this JS file separately 
//  - do not try to import tailwind.css inside of global.scss file
import '@/assets/tailwind.css'
import '@/assets/global.scss'

// -----------------------------------------------
// Vendor Imports
// -----------------------------------------------
import { createApp, watch } from 'vue'
import { createPinia } from 'pinia'
import axios from 'axios'
import ConfirmationService from 'primevue/confirmationservice'
import ToastService from 'primevue/toastservice'
import piniaPluginPersistedState from 'pinia-plugin-persistedstate'
// CASL disabled — pending team review before removal. Frontend now uses role helpers
// from authStore (currentUser.isAdmin / isManager / isDc / isDg).
// import { Can, abilitiesPlugin } from '@casl/vue'
import PrimeVue from 'primevue/config'
// import Aura from '@primeuix/themes/aura'

// -----------------------------------------------
// App Imports
// -----------------------------------------------
import App from './App.vue'
import router from './router'
import { useAuthStore } from '@/stores'
// CASL disabled — see note above.
// import { defineAbilityFor, ability } from '@/utils/ability'
import trmgTheme from '@/trmgTheme'

const app = createApp(App)

// -----------------------------------------------
// App Components & Directives
// -----------------------------------------------
import AdminPageHeader from '@/components/AdminPageHeader.vue'
import AppBreadcrumb from '@/components/AppBreadcrumb.vue'
import AppIcon from '@/components/AppIcon.vue'
import DateInput from '@/components/inputs/DateInput.vue'
import LoadingArea from '@/components/LoadingArea.vue'
import MailTo from '@/components/links/MailTo.vue'
import PageMenu from '@/components/PageMenu.vue'
import Pagination from '@/components/Pagination.vue'
import PaginationMini from '@/components/PaginationMini.vue'
import PasswordChecklist from '@/components/PasswordChecklist.vue'
import TourLink from '@/components/links/TourLink.vue'
import TransfereeLink from '@/components/links/TransfereeLink.vue'
import CompanyLink from '@/components/links/CompanyLink.vue'
import ReloCompanyLink from '@/components/links/ReloCompanyLink.vue'
import UserAvatar from '@/components/UserAvatar.vue'
import UserProfile from '@/components/UserProfile.vue'
import AppCalendar from '@/components/AppCalendar.vue'
import ReportTable from '@/components/reports/ReportTable.vue'
import ReportGroupHeading from '@/components/reports/ReportGroupHeading.vue'
import ReportSubHeading from '@/components/reports/ReportSubHeading.vue'
import ReportColumnHeaders from '@/components/reports/ReportColumnHeaders.vue'
import ReportGroupFooter from '@/components/reports/ReportGroupFooter.vue'
import SearchInput from '@/components/inputs/SearchInput.vue'

app.component('AdminPageHeader', AdminPageHeader)
app.component('AppBreadcrumb', AppBreadcrumb)
app.component('AppIcon', AppIcon)
app.component('DateInput', DateInput)
app.component('MailTo', MailTo)
app.component('LoadingArea', LoadingArea)
app.component('PageMenu', PageMenu)
app.component('Pagination', Pagination)
app.component('PaginationMini', PaginationMini)
app.component('PasswordChecklist', PasswordChecklist)
app.component('TourLink', TourLink)
app.component('TransfereeLink', TransfereeLink)
app.component('CompanyLink', CompanyLink)
app.component('ReloCompanyLink', ReloCompanyLink)
app.component('UserAvatar', UserAvatar)
app.component('UserProfile', UserProfile)
app.component('AppCalendar', AppCalendar)
app.component('ReportTable', ReportTable)
app.component('ReportGroupHeading', ReportGroupHeading)
app.component('ReportSubHeading', ReportSubHeading)
app.component('ReportColumnHeaders', ReportColumnHeaders)
app.component('ReportGroupFooter', ReportGroupFooter)
app.component('SearchInput', SearchInput)
// app.component(Can.name, Can)

// -----------------------------------------------
// Badges
// -----------------------------------------------
import DocumentIcon from '@/components/badges/DocumentIcon.vue'
import TimesheetBadge from '@/components/badges/TimesheetBadge.vue'
import TourPhaseBadge from '@/components/badges/TourPhaseBadge.vue'
import TourCancelBadge from '@/components/badges/TourCancelBadge.vue'
import TourTypeBadge from '@/components/badges/TourTypeBadge.vue'
import TransfereeStatusBadge from '@/components/badges/TransfereeStatusBadge.vue'

app.component('DocumentIcon', DocumentIcon)
app.component('TimesheetBadge', TimesheetBadge)
app.component('TourPhaseBadge', TourPhaseBadge)
app.component('TourCancelBadge', TourCancelBadge)
app.component('TourTypeBadge', TourTypeBadge)
app.component('TransfereeStatusBadge', TransfereeStatusBadge)

// -----------------------------------------------
// Buttons
// -----------------------------------------------
import ConfirmButton from '@/components/buttons/ConfirmButton.vue'
import FollowUpButton from "@/components/buttons/FollowUpButton.vue"
import ScheduleEmailButton from "@/components/buttons/ScheduleEmailButton.vue"
import TimesheetButton from "@/components/buttons/TimesheetButton.vue"
import ZonesMultiSelect from "@/components/buttons/ZonesMultiSelect.vue"

app.component('ConfirmButton', ConfirmButton)
app.component('FollowUpButton', FollowUpButton)
app.component('ScheduleEmailButton', ScheduleEmailButton)
app.component('TimesheetButton', TimesheetButton)
app.component('ZonesMultiSelect', ZonesMultiSelect)

// -----------------------------------------------
// App Form Inputs
// -----------------------------------------------
import AppCheckboxes from "@/components/inputs/AppCheckboxes.vue"
import AppCurrencyInput from "@/components/inputs/AppCurrencyInput.vue"
import AppDateInput from "@/components/inputs/AppDateInput.vue"
import AppDropdown from "@/components/inputs/AppDropdown.vue"
import AppForm from "@/components/inputs/AppForm.vue"
import AppNumberInput from "@/components/inputs/AppNumberInput.vue"
import AppPasswordInput from "@/components/inputs/AppPasswordInput.vue"
import AppSelectButton from "@/components/inputs/AppSelectButton.vue"
import AppTextareaInput from "@/components/inputs/AppTextareaInput.vue"
import AppTextInput from "@/components/inputs/AppTextInput.vue"
import AppTimeInput from "@/components/inputs/AppTimeInput.vue"
import AppToggleInput from "@/components/inputs/AppToggleInput.vue"

app.component('AppCheckboxes', AppCheckboxes)
app.component('AppCurrencyInput', AppCurrencyInput)
app.component('AppDateInput', AppDateInput)
app.component('AppDropdown', AppDropdown)
app.component('AppForm', AppForm)
app.component('AppNumberInput', AppNumberInput)
app.component('AppPasswordInput', AppPasswordInput)
app.component('AppSelectButton', AppSelectButton)
app.component('AppTextareaInput', AppTextareaInput)
app.component('AppTextInput', AppTextInput)
app.component('AppTimeInput', AppTimeInput)
app.component('AppToggleInput', AppToggleInput)

// -----------------------------------------------
// Dropdown inputs
// -----------------------------------------------
import CounselorDropdown from '@/components/dropdowns/CounselorDropdown.vue'
import GuideDropdown from '@/components/dropdowns/GuideDropdown.vue'
import CountryDropdown from '@/components/dropdowns/CountryDropdown.vue'
import GeoStateDropdown from '@/components/dropdowns/GeoStateDropdown.vue'
import ServicesDropdown from '@/components/dropdowns/ServicesDropdown.vue'
import CompanyDropdown from '@/components/dropdowns/CompanyDropdown.vue'
import ReloCompanyDropdown from '@/components/dropdowns/ReloCompanyDropdown.vue'
import DestinationCounselorDropdown from '@/components/dropdowns/DestinationCounselorDropdown.vue'
import DestinationGuideDropdown from '@/components/dropdowns/DestinationGuideDropdown.vue'

app.component('CounselorDropdown', CounselorDropdown)
app.component('GuideDropdown', GuideDropdown)
app.component('CountryDropdown', CountryDropdown)
app.component('GeoStateDropdown', GeoStateDropdown)
app.component('ServicesDropdown', ServicesDropdown)
app.component('CompanyDropdown', CompanyDropdown)
app.component('ReloCompanyDropdown', ReloCompanyDropdown)
app.component('DestinationCounselorDropdown', DestinationCounselorDropdown)
app.component('DestinationGuideDropdown', DestinationGuideDropdown)

// -----------------------------------------------
// PrimeVue Components & Directives
// -----------------------------------------------
import Avatar       from "primevue/avatar"
import Badge        from "primevue/badge"
import Button       from "primevue/button"
import Card         from "primevue/card"
import Checkbox     from 'primevue/checkbox'
import CheckboxGroup from 'primevue/checkboxgroup'
import Drawer       from "primevue/drawer"
import InputGroup   from 'primevue/inputgroup'
import InputGroupAddon from 'primevue/inputgroupaddon'
import InputText    from "primevue/inputtext"
import InputNumber  from "primevue/inputnumber"

import MegaMenu     from "primevue/megamenu"
import Menu         from "primevue/menu"
import Menubar      from "primevue/menubar"
import Popover      from "primevue/popover"
import Select       from "primevue/select"
import SelectButton from "primevue/selectbutton"
import Skeleton     from 'primevue/skeleton'
import Tab          from 'primevue/tab'
import Tabs         from 'primevue/tabs'
import TabList      from 'primevue/tablist'
import Tag          from 'primevue/tag'
import Textarea     from 'primevue/textarea'
import Toast        from 'primevue/toast'
import ToggleSwitch from 'primevue/toggleswitch'
import RadioButton from 'primevue/radiobutton'
import RadioButtonGroup from 'primevue/radiobuttongroup'
import FileUpload          from 'primevue/fileupload'
import Message             from 'primevue/message'

app.component('Avatar', Avatar)
app.component('Badge', Badge)
app.component('Button', Button)
app.component('Card', Card)
app.component('Checkbox', Checkbox)
app.component('CheckboxGroup', CheckboxGroup)
app.component('Drawer', Drawer)
app.component('InputGroup', InputGroup)
app.component('InputGroupAddon', InputGroupAddon)
app.component('InputText', InputText)
app.component('InputNumber', InputNumber)
app.component('MegaMenu', MegaMenu)
app.component('Menu', Menu)
app.component('Menubar', Menubar)
app.component('Popover', Popover)
app.component('Select', Select)
app.component('SelectButton', SelectButton)
app.component('Skeleton', Skeleton)
app.component('Tab', Tab)
app.component('Tabs', Tabs)
app.component('TabList', TabList)
app.component('Tag', Tag)
app.component('Textarea', Textarea)
app.component('Toast', Toast)
app.component('ToggleSwitch', ToggleSwitch)
app.component('RadioButton', RadioButton)
app.component('RadioButtonGroup', RadioButtonGroup)
app.component('FileUpload', FileUpload)
app.component('Message', Message)

import Ripple from 'primevue/ripple'
import Tooltip from 'primevue/tooltip'
app.directive('ripple', Ripple)
app.directive('tooltip', Tooltip)
// -----------------------------------------------
// DayJS
// -----------------------------------------------
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
// import quarterOfYear from 'dayjs/plugin/quarterOfYear'
import relativeTime from 'dayjs/plugin/relativeTime'
import objectSupport from 'dayjs/plugin/objectSupport'
import duration from 'dayjs/plugin/duration'
import isoWeek from 'dayjs/plugin/isoWeek';

// dayjs.extend(quarterOfYear)
dayjs.extend(objectSupport)
dayjs.extend(relativeTime)
dayjs.extend(utc)
dayjs.extend(duration)
dayjs.extend(isoWeek);
window.dayjs = dayjs

// ---------------------------------------------------
// Use Services
// ---------------------------------------------------
const pinia = createPinia()
pinia.use(piniaPluginPersistedState)
app.use(pinia)
app.use(router)
app.use(ToastService)
// CASL disabled — see note at top of file.
// app.use(abilitiesPlugin, ability)
app.use(ConfirmationService)

const authStore = useAuthStore()

// CASL disabled — see note at top of file.
// const rules = defineAbilityFor(authStore.userData)
// ability.update(rules.rules)

// Custom axios configuration
watch(() => authStore.token, (newToken, oldToken) => {
  axios.defaults.headers.common['Authorization'] = newToken
})

app.use(PrimeVue, {
  theme: {
    preset: trmgTheme,
    options: {
      darkModeSelector: '.dark-mode',
      // CSS @layers + Tailwind v4:
      // Ensure primevue layer is after theme and base, but before the other Tailwind layers such as utilities.
      cssLayer: {
        name: 'primevue',
        order: 'theme, base, primevue, properties, components, utilities, keyframes'
      },
    },
  },
  ripple: true,
})

app.mount('#vue-mount-container')
